import React, { useCallback, useRef, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  Send,
  User,
  Loader2,
  Wrench,
  CheckCircle2,
  Cpu,
  Paperclip,
  X,
  FileText,
  Sparkles,
  Pencil,
  Eraser,
  Play,
  Save,
  Download,
  Search,
  FileSearch,
  ListChecks,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  FileSpreadsheet,
  Brain,
  Forward,
} from 'lucide-react';
import LineSideIcon from './LineSideIcon';
import ContextUsageBar from './ContextUsageBar';
import AgentAvatar from 'features/agents/AgentAvatar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { authFetch } from 'shared/api/authFetch';
import { admin_apiConfig } from 'shared/config/config';
import { Link } from 'react-router-dom';
import { initiativePath } from 'features/initiatives/initiativePaths';
import CredentialSetupContinueCard from '../credentials/CredentialSetupContinueCard';
import AgentCard from '../cards/AgentCard';
import { agentOutcomes } from './agentOutcomes';
import { AGENT_BRAND } from 'shared/branding';
import { PrismAsync as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { formatClock } from 'shared/time/businessTime';
import useVoiceInput from '../useVoiceInput';
import { VoiceInputButtons, VoiceInputDetails } from './VoiceInputControls';

const DEFAULT_SUGGESTIONS = [
  'Help me bring in my spreadsheet',
  'Connect a system my team uses',
  'Check what this process needs before my team can use it',
  'Show me which work needs attention',
  'Help me improve a handoff between people',
];

const DEFAULT_CONFIG = {
  title: AGENT_BRAND,
  subtitle: 'I can help set up your workspace, bring in your data, and improve how your team works.',
  suggestions: DEFAULT_SUGGESTIONS,
  placeholder: 'Ask or assign work…',
};

const DEFAULT_THEME = {
  userBubble: 'bg-gradient-to-br from-sky-500 to-sky-600',
  assistantBubble: 'bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-200/80',
  avatarUser: 'bg-sky-400/90',
  avatarAssistant: 'bg-white ring-1 ring-gray-200/80',
  suggestionBtn: 'hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200',
  typingText: 'text-sky-600',
  typingDot: 'bg-sky-500',
  timestamp: 'text-sky-500',
  sendBtn: 'bg-sky-500 hover:bg-sky-600',
  inputFocus: 'focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-50',
  contextLoader: 'text-sky-500',
  toolSpinner: 'text-sky-500',
  toolIcon: 'text-sky-600',
  toolBorder: 'border-sky-200/50',
  toolHeader: 'border-sky-100',
  codeBg: 'bg-sky-50 text-sky-900',
  blockquote: 'border-sky-300 bg-sky-50/50',
  link: 'text-sky-600 hover:text-sky-800',
};

// ── Friendly activity labels ───────────────────────────────────────────
// Turns raw tool names (e.g. "load_workspace_data") into plain-language
// activity phrases the customer can understand ("Reading your data").
// Generic, verb-prefix driven so new tools degrade gracefully; a small set
// of overrides polishes the most common platform tools.

const TOOL_VERB_RULES = [
  { prefixes: ['create', 'add', 'build', 'generate', 'make', 'author', 'draft', 'new', 'stage'], verb: 'Creating', icon: Sparkles },
  { prefixes: ['update', 'patch', 'edit', 'modify', 'set', 'rename', 'configure', 'adjust', 'reconcile', 'mutate', 'sync'], verb: 'Updating', icon: Pencil },
  { prefixes: ['delete', 'remove', 'reset', 'clear', 'drop'], verb: 'Cleaning up', icon: Eraser },
  { prefixes: ['run', 'execute', 'invoke', 'call', 'compute', 'analyze', 'analyse'], verb: 'Running', icon: Play },
  { prefixes: ['export', 'save', 'commit', 'publish', 'persist', 'apply', 'deploy'], verb: 'Saving', icon: Save },
  { prefixes: ['import', 'upload', 'ingest', 'load', 'fetch', 'hydrate'], verb: 'Loading', icon: Download },
  { prefixes: ['search', 'find', 'lookup', 'resolve', 'query', 'discover'], verb: 'Searching', icon: Search },
  { prefixes: ['get', 'retrieve', 'view', 'list', 'read', 'inspect', 'summarize', 'summarise', 'review', 'check', 'describe', 'preview'], verb: 'Reviewing', icon: FileSearch },
];

// Polished phrasing for a few high-traffic platform tools. These are generic
// platform tool names (workspace/program/action/skill), not business ontology
// names — copy only, no runtime behavior depends on them.
const TOOL_LABEL_OVERRIDES = {
  load_workspace_data: 'Reading your data',
  load_lineage_data: 'Tracing history',
  workspace_environment_summary: 'Reviewing the workspace',
  inspect_workspace: 'Inspecting your data',
  mutate_workspace: 'Updating the workspace',
  run_program: 'Running analysis',
  export_to_database: 'Saving changes',
  load_skill: 'Getting set up',
};

/** A notice that older messages were summarized for the agent. */
export const isCompactionNotice = (block) =>
  block?.type === 'notice' && Boolean(block.meta?.compactionId || block.meta?.kind === 'context_compacted');

// A conversation with an agent never ends, so the panel opens on its recent part.
const RECENT_MESSAGES = 40;
const MIN_VISIBLE_MESSAGES = 12;

/**
 * How many messages to fold away: those before the latest summary, or all but
 * the last RECENT_MESSAGES, whichever is more, always leaving a few to read.
 */
export const foldedMessageCount = (messages = []) => {
  let lastSummary = -1;
  messages.forEach((msg, index) => {
    if ((msg?.blocks || []).some(isCompactionNotice)) lastSummary = index;
  });
  const fold = Math.max(lastSummary, messages.length - RECENT_MESSAGES, 0);
  return Math.max(0, Math.min(fold, messages.length - MIN_VISIBLE_MESSAGES));
};

const CompactionDivider = () => (
  <div className="flex items-center gap-3 px-2 py-1 text-[11px] text-gray-400" data-testid="conversation-summarized">
    <span className="h-px flex-1 bg-gray-200" />
    <span className="shrink-0">Earlier messages summarized to keep things quick</span>
    <span className="h-px flex-1 bg-gray-200" />
  </div>
);

const humanizeTokens = (tokens) =>
  tokens
    .join(' ')
    .replace(/\bids?\b/gi, 'IDs')
    .trim();

const getToolActivity = (toolName) => {
  const key = String(toolName || '').trim().toLowerCase();
  if (TOOL_LABEL_OVERRIDES[key]) {
    return { label: TOOL_LABEL_OVERRIDES[key], Icon: Wrench };
  }
  const tokens = key.split(/[_\s]+/).filter(Boolean);
  if (tokens.length === 0) return { label: 'Working', Icon: Wrench };
  const rule = TOOL_VERB_RULES.find((r) => r.prefixes.includes(tokens[0]));
  if (rule) {
    const rest = humanizeTokens(tokens.slice(1));
    return { label: rest ? `${rule.verb} ${rest}` : rule.verb, Icon: rule.icon };
  }
  return { label: `Working on ${humanizeTokens(tokens)}`, Icon: Wrench };
};

const getSubAgentLabel = (block) => {
  const raw = block?.agentIdentifier || block?.agentName || 'agent';
  const friendly = String(raw)
    .replace(/_/g, ' ')
    .replace(/\bagent\b/gi, '')
    .trim();
  return friendly ? `Working with ${friendly}` : 'Working with an Agent';
};

const MAX_DETAIL_CHARS = 3000;

const stringifyDetail = (value) => {
  if (value === null || value === undefined) return '';
  let text;
  if (typeof value === 'string') {
    text = value;
  } else {
    try {
      text = JSON.stringify(value, null, 2);
    } catch (_) {
      text = String(value);
    }
  }
  if (text.length > MAX_DETAIL_CHARS) {
    return `${text.slice(0, MAX_DETAIL_CHARS)}\n… (truncated)`;
  }
  return text;
};

const buildToolDetail = (block, resultBlock) => {
  const sections = [];
  if (block.args && typeof block.args === 'object' && Object.keys(block.args).length > 0) {
    sections.push({ heading: 'Input', body: stringifyDetail(block.args) });
  }
  const result = block.result ?? resultBlock?.result;
  if (result !== null && result !== undefined && result !== '') {
    sections.push({ heading: 'Result', body: stringifyDetail(result) });
  }
  return sections;
};

const createMarkdownComponents = (isUser, theme = DEFAULT_THEME) => ({
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';

    if (!inline && language) {
      return (
        <div className="my-2 rounded overflow-hidden border border-gray-200">
          <div className="bg-gray-800 px-2 py-1 text-xs font-mono text-gray-300">{language}</div>
          <SyntaxHighlighter
            language={language}
            style={vscDarkPlus}
            customStyle={{ margin: 0, borderRadius: 0, fontSize: '0.875rem', lineHeight: '1.5' }}
            codeTagProps={{ style: { fontSize: '0.875rem' } }}
            {...props}
          >
            {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
        </div>
      );
    }

    return (
      <code
        className={`${isUser ? 'bg-white/20 text-white' : theme.codeBg} px-1 py-0.5 rounded text-[0.95em] font-mono`}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre({ children }) {
    return <div className="overflow-x-auto">{children}</div>;
  },
  p({ children }) {
    return <p className="mb-2 last:mb-0 leading-relaxed text-sm">{children}</p>;
  },
  ul({ children }) {
    return <ul className="list-disc list-inside mb-2 space-y-0.5 ml-1 text-sm">{children}</ul>;
  },
  ol({ children }) {
    return <ol className="list-decimal list-inside mb-2 space-y-0.5 ml-1 text-sm">{children}</ol>;
  },
  blockquote({ children }) {
    return (
      <blockquote
        className={`border-l-2 pl-2 py-1 my-2 italic text-sm ${
          isUser ? 'border-white/40 text-white/90' : `${theme.blockquote} rounded-r`
        }`}
      >
        {children}
      </blockquote>
    );
  },
  a({ href, children }) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`underline ${isUser ? 'text-white hover:text-white/80' : theme.link}`}
      >
        {children}
      </a>
    );
  },
});

const USER_MARKDOWN_COMPONENTS = createMarkdownComponents(true);

const formatFileSize = (sizeBytes) => {
  const size = Number(sizeBytes);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const downloadChatArtifact = async (block) => {
  const downloadPath = String(block?.downloadPath || '').trim();
  if (!downloadPath) {
    throw new Error('Download path is unavailable for this attachment.');
  }
  const response = await authFetch(`${admin_apiConfig.baseURL}${downloadPath}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }));
    const message = typeof err?.detail === 'string'
      ? err.detail
      : err?.detail?.message || `Download failed (${response.status})`;
    throw new Error(message);
  }
  const blob = await response.blob();
  const filename = String(block?.filename || block?.label || 'download').trim() || 'download';
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
};

const getArtifactKindLabel = (contentType, filename) => {
  const ct = String(contentType || '').toLowerCase();
  const name = String(filename || '').toLowerCase();
  if (ct.includes('pdf') || name.endsWith('.pdf')) return 'PDF report';
  if (ct.includes('csv') || name.endsWith('.csv')) return 'CSV export';
  if (ct.includes('spreadsheet') || name.endsWith('.xlsx') || name.endsWith('.xls')) {
    return 'Spreadsheet';
  }
  return 'Downloadable file';
};

const ReportArtifactCard = ({ block, theme, isUser = false }) => {
  const sizeLabel = formatFileSize(block.sizeBytes);
  const displayName = block.label || block.filename || 'Report';
  const kindLabel = getArtifactKindLabel(block.contentType, block.filename);
  const isCsv =
    String(block.contentType || '').toLowerCase().includes('csv') ||
    String(block.filename || '').toLowerCase().endsWith('.csv');
  const Icon = isCsv ? FileSpreadsheet : FileText;
  const iconWrapClass = isUser
    ? 'bg-white/15 ring-white/25'
    : isCsv
      ? 'bg-emerald-50 ring-emerald-100'
      : 'bg-red-50 ring-red-100';
  const iconClass = isUser ? 'text-white' : isCsv ? 'text-emerald-600' : 'text-red-600';

  const handleDownload = () => {
    downloadChatArtifact(block).catch((error) => {
      window.alert(error?.message || 'Download failed.');
    });
  };

  return (
    <div
      className={`my-1.5 w-full overflow-hidden rounded-lg border shadow-sm ${
        isUser
          ? 'border-white/35 bg-white/10'
          : `${theme.toolBorder} bg-gradient-to-br from-white via-white to-gray-50/90`
      }`}
    >
      <div className="flex items-center gap-2.5 p-3">
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ring-1 ${iconWrapClass}`}
        >
          <Icon size={18} className={iconClass} />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className={`text-[10px] font-semibold uppercase tracking-wide ${
              isUser ? 'text-white/80' : theme.typingText
            }`}
          >
            Report ready
          </div>
          <div
            className={`truncate text-sm font-semibold ${isUser ? 'text-white' : 'text-gray-900'}`}
          >
            {displayName}
          </div>
          <div className={`text-[11px] ${isUser ? 'text-white/70' : 'text-gray-500'}`}>
            {[kindLabel, sizeLabel].filter(Boolean).join(' · ')}
          </div>
        </div>
        <button
          type="button"
          onClick={handleDownload}
          className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
            isUser
              ? 'bg-white text-sky-700 hover:bg-white/90'
              : `${theme.sendBtn} text-white shadow-sm hover:opacity-95`
          }`}
        >
          <Download size={14} />
          Download
        </button>
      </div>
    </div>
  );
};

// ── Refined activity timeline ──────────────────────────────────────────
// Customer-facing rendering of the agent's "process" blocks (thinking, tool
// calls, sub-agents). Shows what the agent is doing in plain language with a
// clear sense of progress, while keeping raw thinking/JSON tucked away.

const PROCESS_BLOCK_TYPES = new Set(['thinking', 'tool_call', 'tool_result', 'sub_agent']);

// Status filler some providers send as text; the activity card already says it.
const isWorkFiller = (text) => /^(requesting \d+ tool call\(s\)|tool execution completed)\.?$/i.test(String(text || '').trim());

/**
 * Whether a reply shows anything when how the agent works is hidden: its words,
 * cards, files and notices, or what it did that reaches beyond the chat.
 */
const showsWithoutWork = (blocks) =>
  blocks.some((block) =>
    block.type === 'text' || block.type === 'final_response'
      ? Boolean(String(block.text || '').trim()) && !isWorkFiller(block.text)
      : !PROCESS_BLOCK_TYPES.has(block.type)
  ) || agentOutcomes(blocks).length > 0;

/** While the agent works and its steps are hidden: one quiet line. */
const WorkingLine = ({ theme }) => (
  <div className="flex items-center gap-2 px-1 py-1 text-xs text-gray-500" data-testid="agent-working">
    <Loader2 size={13} className={`${theme.toolSpinner} animate-spin`} />
    <span>Working on it…</span>
  </div>
);

const StatusDot = ({ status, theme }) => {
  if (status === 'running') {
    return <Loader2 size={13} className={`${theme.toolSpinner} animate-spin`} />;
  }
  if (status === 'interrupted') return <AlertTriangle size={13} className="text-amber-600" aria-label="Outcome unconfirmed" />;
  if (status === 'failed' || status === 'error') {
    return <AlertTriangle size={13} className="text-rose-500" />;
  }
  return <CheckCircle2 size={13} className="text-emerald-500" />;
};

const getThinkingBlockKey = (block, fallbackIdx = 0) => block?.id || `thinking-${fallbackIdx}`;

const resolveActiveThinkingBlockId = (blocks) => {
  if (!Array.isArray(blocks)) return null;
  let activeKey = null;
  let thinkingIdx = 0;
  blocks.forEach((block) => {
    if (block?.type === 'thinking') {
      activeKey = getThinkingBlockKey(block, thinkingIdx);
      thinkingIdx += 1;
    }
  });
  return activeKey;
};

const ActivityThinkingRow = ({ block, isActive, theme }) => {
  const [open, setOpen] = useState(false);
  const text = block?.text || '';
  const preview = text.trim().slice(0, 48);
  const label = preview
    ? `Thinking: ${preview}${text.trim().length > 48 ? '…' : ''}`
    : isActive
      ? 'Thinking…'
      : 'Thought it through';

  return (
    <div>
      <button
        type="button"
        onClick={() => text.trim() && setOpen((current) => !current)}
        className={`group flex w-full items-center gap-2 rounded-md py-1 pl-1 pr-1.5 text-left transition-colors ${
          text.trim() ? 'hover:bg-black/[0.03] cursor-pointer' : 'cursor-default'
        }`}
        aria-expanded={text.trim() ? open : undefined}
      >
        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
          {isActive && !text.trim() ? (
            <Loader2 size={13} className={`${theme.toolSpinner} animate-spin`} />
          ) : (
            <Sparkles size={13} className={isActive ? 'text-amber-400' : 'text-gray-300'} />
          )}
        </span>
        <span
          className={`flex-1 min-w-0 truncate text-xs italic ${
            isActive ? 'font-medium text-gray-600' : 'text-gray-400'
          }`}
        >
          {label}
        </span>
        {text.trim() ? (
          open ? (
            <ChevronDown size={13} className="flex-shrink-0 text-gray-300 group-hover:text-gray-400" />
          ) : (
            <ChevronRight size={13} className="flex-shrink-0 text-gray-300 group-hover:text-gray-400" />
          )
        ) : null}
      </button>
      {open && text.trim() ? (
        <div role="region" aria-label="Thinking details" className="max-h-48 overflow-y-auto overscroll-contain">
          <div className="ml-7 mb-1 mt-0.5 rounded-md border border-amber-200/50 bg-amber-50/60 p-1.5">
            <div className="text-xs text-amber-900 prose prose-sm max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={createMarkdownComponents(false, theme)}>
                {text}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ActivityToolRow = ({ block, resultBlock, theme, turnIsStreaming = true }) => {
  const [userOpen, setUserOpen] = useState(false);
  const { label, Icon } = getToolActivity(block.toolName);
  const isRunning = turnIsStreaming && block.status === 'running';
  const isError = block.isError || resultBlock?.isError || block.status === 'failed';
  const status = isRunning ? 'running' : isError ? 'failed' : block.status === 'interrupted' ? 'interrupted' : 'completed';
  const sections = buildToolDetail(block, resultBlock);
  const hasDetail = sections.length > 0;
  const open = userOpen;
  const toolResult = block.result ?? resultBlock?.result;

  return (
    <div>
      <button
        type="button"
        onClick={() => hasDetail && setUserOpen((current) => !current)}
        className={`group flex w-full items-center gap-2 rounded-md py-1 pl-1 pr-1.5 text-left transition-colors ${
          hasDetail ? 'hover:bg-black/[0.03] cursor-pointer' : 'cursor-default'
        }`}
        aria-expanded={hasDetail ? open : undefined}
      >
        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
          <StatusDot status={status} theme={theme} />
        </span>
        <Icon size={13} className={`flex-shrink-0 ${isRunning ? theme.toolIcon : 'text-gray-400'}`} />
        <span className={`flex-1 min-w-0 truncate text-xs ${isRunning ? 'font-medium text-gray-700' : 'text-gray-600'}`}>
          {label}
        </span>
        {hasDetail ? (
          open ? (
            <ChevronDown size={13} className="flex-shrink-0 text-gray-300 group-hover:text-gray-400" />
          ) : (
            <ChevronRight size={13} className="flex-shrink-0 text-gray-300 group-hover:text-gray-400" />
          )
        ) : null}
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          open && hasDetail ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="ml-7 mb-1 mt-0.5 space-y-1.5">
            {sections.map((section) => (
              <div key={section.heading}>
                <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{section.heading}</div>
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md border border-gray-200/70 bg-gray-50 p-1.5 text-[11px] leading-snug text-gray-600">
                  {section.body}
                </pre>
              </div>
            ))}
          </div>
        </div>
      </div>
      <CredentialSetupContinueCard
        toolName={block.toolName}
        toolResult={toolResult}
      />
    </div>
  );
};

const ActivitySubAgentRow = ({ block, theme, activeThinkingBlockId = null, turnIsStreaming = true }) => {
  const innerBlocks = Array.isArray(block.blocks) ? block.blocks : [];
  const isRunning = turnIsStreaming && block.status === 'running';
  const status = isRunning ? 'running' : ['failed', 'interrupted'].includes(block.status) ? block.status : 'completed';
  const label = getSubAgentLabel(block);

  return (
    <div>
      <div className="flex items-center gap-2 py-1 pl-1">
        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
          <StatusDot status={status} theme={theme} />
        </span>
        <Cpu size={13} className={`flex-shrink-0 ${isRunning ? 'text-indigo-500' : 'text-gray-400'}`} />
        <span className={`flex-1 min-w-0 truncate text-xs ${isRunning ? 'font-medium text-gray-700' : 'text-gray-600'}`}>
          {label}
        </span>
      </div>
      {block.error ? (
        <div className="ml-7 mb-1 rounded-md border border-rose-200/60 bg-rose-50 p-1.5 text-[11px] text-rose-600">
          {block.error}
        </div>
      ) : null}
      {innerBlocks.length > 0 ? (
        <div className="ml-3 border-l border-gray-200 pl-3">
          <OrderedBlocks blocks={innerBlocks} isUser={false} theme={theme} activeThinkingBlockId={activeThinkingBlockId} turnIsStreaming={turnIsStreaming} />
        </div>
      ) : isRunning ? (
        <div className="ml-7 text-[11px] italic text-gray-400">Getting started…</div>
      ) : null}
    </div>
  );
};

const splitProcessBlocksIntoCycles = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length === 0) return [];

  const cycles = [];
  let current = [];

  blocks.forEach((block) => {
    if (!block?.type) return;
    if (block.type === 'thinking' && current.length > 0) {
      const hasNonThinking = current.some((b) => b.type !== 'thinking');
      if (hasNonThinking) {
        cycles.push(current);
        current = [block];
        return;
      }
    }
    current.push(block);
  });

  if (current.length > 0) cycles.push(current);
  return cycles;
};

const OUTCOME_ICONS = { memory: Brain, handoff: Forward, started: Play, step: CheckCircle2 };

/** What the agent did that carries beyond this chat, shown outside the activity card. */
const AgentOutcomes = ({ blocks }) => {
  const outcomes = useMemo(() => agentOutcomes(blocks), [blocks]);
  if (!outcomes.length) return null;
  return (
    <div className="space-y-1.5 px-1" data-testid="agent-outcomes">
      {outcomes.map((outcome) => {
        if (outcome.kind === 'card') return <AgentCard key={outcome.key} card={outcome.card} />;
        const Icon = OUTCOME_ICONS[outcome.kind] || CheckCircle2;
        const body = (
          <>
            <Icon size={12} className="shrink-0 text-slate-400" />
            <span className="truncate">{outcome.text}</span>
          </>
        );
        const chip = 'inline-flex max-w-full items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600';
        return (
          <div key={outcome.key} data-testid={`agent-outcome-${outcome.kind}`}>
            {outcome.initiativeId ? (
              <Link to={initiativePath(outcome.initiativeId)} className={`${chip} hover:bg-slate-200 hover:text-slate-900`}>{body}</Link>
            ) : (
              <span className={chip} title={outcome.text}>{body}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ActivityGroup = ({ blocks, theme, isLatestGroup = true, activeThinkingBlockId = null, turnIsStreaming = true }) => {
  const [userOpen, setUserOpen] = useState(null);

  useEffect(() => {
    if (!isLatestGroup) setUserOpen(null);
  }, [isLatestGroup]);

  const resultByCallId = {};
  blocks.forEach((b) => {
    if (b.type === 'tool_result' && b.toolCallId) resultByCallId[b.toolCallId] = b;
  });
  const callIds = new Set(blocks.filter((b) => b.type === 'tool_call' && b.toolCallId).map((b) => b.toolCallId));

  // Build ordered rows, keeping each thinking beat separate and folding
  // tool_result blocks into their originating tool_call.
  const rows = [];
  let thinkingIdx = 0;
  blocks.forEach((b) => {
    if (b.type === 'thinking') {
      rows.push({ kind: 'thinking', key: getThinkingBlockKey(b, thinkingIdx), block: b });
      thinkingIdx += 1;
    } else if (b.type === 'tool_call') {
      rows.push({ kind: 'tool', key: b.id, block: b, result: resultByCallId[b.toolCallId] || null });
    } else if (b.type === 'sub_agent') {
      rows.push({ kind: 'sub', key: b.id, block: b });
    } else if (b.type === 'tool_result') {
      if (b.toolCallId && callIds.has(b.toolCallId)) return; // folded into its tool_call
      rows.push({ kind: 'tool', key: b.id, block: { toolName: b.toolName, status: 'completed', args: {}, result: b.result, isError: b.isError }, result: null });
    }
  });

  const stepRows = rows.filter((r) => r.kind === 'tool' || r.kind === 'sub');
  const isRunning =
    turnIsStreaming &&
    blocks.some(
      (b) => (b.type === 'tool_call' || b.type === 'sub_agent') && b.status === 'running'
    );
  // The latest card stays open so the work streams in live; earlier ones fold away.
  const groupOpen = isLatestGroup ? (userOpen ?? true) : userOpen === true;

  // No tool/sub-agent steps yet — show thinking inside a card so prior cycles collapse cleanly.
  if (stepRows.length === 0) {
    const thinkingRows = rows.filter((r) => r.kind === 'thinking');
    if (thinkingRows.length === 0) {
      return (
        <div className="flex items-center gap-2 px-1 py-1 text-xs text-gray-400">
          <Loader2 size={13} className={`${theme.toolSpinner} animate-spin`} />
          <span className="italic">Thinking…</span>
        </div>
      );
    }

    const hasActiveThinking = thinkingRows.some((r) => r.key === activeThinkingBlockId);
    return (
      <div className="my-1 overflow-hidden rounded-lg border border-gray-200/70 bg-gray-50/40">
        <button
          type="button"
          onClick={() => setUserOpen((current) => (current === null ? !groupOpen : !current))}
          className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-black/[0.03]"
          aria-expanded={groupOpen}
        >
          <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
            {hasActiveThinking ? (
              <Loader2 size={14} className={`${theme.toolSpinner} animate-spin`} />
            ) : (
              <Sparkles size={14} className="text-gray-300" />
            )}
          </span>
          <span
            className={`flex-1 min-w-0 truncate text-xs font-medium ${
              hasActiveThinking ? 'text-gray-700' : 'text-gray-500'
            }`}
          >
            {hasActiveThinking ? 'Thinking…' : 'Thought it through'}
          </span>
          {groupOpen ? (
            <ChevronDown size={14} className="flex-shrink-0 text-gray-300" />
          ) : (
            <ChevronRight size={14} className="flex-shrink-0 text-gray-300" />
          )}
        </button>
        <div
          className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
            groupOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="overflow-hidden">
            <div className="space-y-0.5 border-t border-gray-100 px-2 py-1.5">
              {thinkingRows.map((row) => (
                <ActivityThinkingRow
                  key={row.key}
                  block={row.block}
                  isActive={row.key === activeThinkingBlockId}
                  theme={theme}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const completedCount = stepRows.filter(
    (r) => r.block.status === 'completed'
  ).length;
  const runningStep = turnIsStreaming ? stepRows.find((r) => r.block.status === 'running') : null;
  const runningLabel = runningStep
    ? runningStep.kind === 'sub'
      ? getSubAgentLabel(runningStep.block)
      : getToolActivity(runningStep.block.toolName).label
    : null;

  const open = groupOpen;
  const headerLabel = isRunning
    ? runningLabel || 'Working…'
    : `Worked through ${stepRows.length} step${stepRows.length === 1 ? '' : 's'}`;

  return (
    <div className="my-1 overflow-hidden rounded-lg border border-gray-200/70 bg-gray-50/40">
      <button
        type="button"
        onClick={() => setUserOpen((current) => (current === null ? !open : !current))}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-black/[0.03]"
        aria-expanded={open}
      >
        <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
          {isRunning ? (
            <Loader2 size={14} className={`${theme.toolSpinner} animate-spin`} />
          ) : (
            <ListChecks size={14} className="text-gray-400" />
          )}
        </span>
        <span className={`flex-1 min-w-0 truncate text-xs font-medium ${isRunning ? 'text-gray-700' : 'text-gray-500'}`}>
          {headerLabel}
        </span>
        {isRunning && completedCount > 0 ? (
          <span className="flex-shrink-0 text-[10px] font-medium text-gray-400">{completedCount} done</span>
        ) : null}
        {open ? (
          <ChevronDown size={14} className="flex-shrink-0 text-gray-300" />
        ) : (
          <ChevronRight size={14} className="flex-shrink-0 text-gray-300" />
        )}
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="space-y-0.5 border-t border-gray-100 px-2 py-1.5">
            {rows.map((row) => {
              if (row.kind === 'thinking') {
                return (
                  <ActivityThinkingRow
                    key={row.key}
                    block={row.block}
                    isActive={row.key === activeThinkingBlockId}
                    theme={theme}
                  />
                );
              }
              if (row.kind === 'sub') {
                return (
                  <ActivitySubAgentRow
                    key={row.key}
                    block={row.block}
                    theme={theme}
                    activeThinkingBlockId={activeThinkingBlockId}
                    turnIsStreaming={turnIsStreaming}
                  />
                );
              }
              return (
                <ActivityToolRow
                  key={row.key}
                  block={row.block}
                  resultBlock={row.result}
                  theme={theme}
                  turnIsStreaming={turnIsStreaming}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderedBlocks = ({
  blocks = [],
  isUser = false,
  theme = DEFAULT_THEME,
  activeThinkingBlockId = undefined,
  turnIsStreaming = true,
  onNewSession = null,
  showWork = true,
}) => {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;

  // An explicit value (including null) wins; only fall back when truly unspecified.
  const resolvedActiveThinkingBlockId =
    activeThinkingBlockId === undefined ? resolveActiveThinkingBlockId(blocks) : activeThinkingBlockId;

  // Segment into content blocks and per-cycle activity cards (thinking → tools).
  // Each new thinking beat after tools starts a fresh card; only the latest stays expanded.
  const segments = [];
  let processRun = [];

  const flushProcessRun = () => {
    if (processRun.length === 0) return;
    splitProcessBlocksIntoCycles(processRun).forEach((cycleBlocks) => {
      segments.push({ kind: 'process', blocks: cycleBlocks });
    });
    processRun = [];
  };

  blocks.forEach((block) => {
    if (!block || !block.type) return;
    if (PROCESS_BLOCK_TYPES.has(block.type)) {
      processRun.push(block);
    } else {
      flushProcessRun();
      segments.push({ kind: 'content', block });
    }
  });
  flushProcessRun();

  const downloadableReportSegments = [];
  const orderedSegments = [];
  segments.forEach((segment) => {
    if (
      segment.kind === 'content' &&
      segment.block?.type === 'attachment' &&
      segment.block?.downloadPath
    ) {
      downloadableReportSegments.push(segment);
      return;
    }
    orderedSegments.push(segment);
  });
  orderedSegments.push(...downloadableReportSegments);

  const latestProcessSegmentIdx = orderedSegments.reduce(
    (latest, segment, idx) => (segment.kind === 'process' ? idx : latest),
    -1
  );

  const renderContentBlock = (block, idx) => {
    if (block.type === 'text' || block.type === 'final_response') {
      if (isWorkFiller(block.text)) return null;
      return (
        <div key={block.id || idx} className={`px-2 pb-2 pt-0 text-sm ${isUser ? 'text-white' : 'text-gray-800'}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={isUser ? USER_MARKDOWN_COMPONENTS : createMarkdownComponents(false, theme)}
          >
            {block.text || ''}
          </ReactMarkdown>
        </div>
      );
    }

    if (block.type === 'agent_card') {
      return (
        <div key={block.id || idx} className="px-1 py-1">
          <AgentCard card={block.card} />
        </div>
      );
    }

    if (block.type === 'attachment') {
      const sizeLabel = formatFileSize(block.sizeBytes);
      const displayName = block.label || block.filename || 'attachment';
      if (block.downloadPath) {
        return (
          <div key={block.id || idx} className="-mx-1 px-1">
            <ReportArtifactCard block={block} theme={theme} isUser={isUser} />
          </div>
        );
      }
      return (
        <div
          key={block.id || idx}
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs ${
            isUser
              ? 'border-white/30 bg-white/10 text-white'
              : 'border-gray-200 bg-gray-50 text-gray-700'
          }`}
        >
          <FileText size={12} className="flex-shrink-0" />
          <span className="truncate max-w-[220px]">{block.filename || 'attachment'}</span>
          {sizeLabel ? <span className={isUser ? 'text-white/70' : 'text-gray-400'}>{sizeLabel}</span> : null}
        </div>
      );
    }

    if (isCompactionNotice(block)) return <CompactionDivider key={block.id || idx} />;

    if (block.type === 'notice') {
          const text =
            block.text ||
            'OpenAI compressed older conversation context so this chat can continue. Your full message history is still available here.';
          const meta = block.meta || {};
          const isServerStatus = meta.kind === 'server_status';
          const isModelFallback = meta.kind === 'model_fallback';
          const isContextOverflow = meta.kind === 'context_window_exceeded';
          const isLegacyRollover = Boolean(meta.childSessionId);
          // Which model answered is how the agent works, not the conversation.
          if (isModelFallback && !showWork) return null;
          if (isServerStatus) {
            return (
              <div key={block.id || idx} className="flex items-center gap-2 px-1 py-1 text-xs text-gray-400">
                <Loader2 size={13} className={`${theme.toolSpinner} animate-spin`} />
                <span className="italic">{text}</span>
              </div>
            );
          }
          const detailBits = [];
          if (Number.isFinite(Number(meta.collapsedMessages))) {
            detailBits.push(`${Number(meta.collapsedMessages).toLocaleString()} messages collapsed`);
          }
          if (
            Number.isFinite(Number(meta.beforeTokensEst)) &&
            Number.isFinite(Number(meta.afterTokensEst))
          ) {
            detailBits.push(
              `${Number(meta.beforeTokensEst).toLocaleString()} → ${Number(meta.afterTokensEst).toLocaleString()} est tokens`
            );
          }
          if (meta.compactionId) {
            detailBits.push(`compaction ${String(meta.compactionId).slice(0, 8)}`);
          }
          if (isLegacyRollover) {
            detailBits.push(`next session ${String(meta.childSessionId).slice(0, 8)}`);
          }
          if (isModelFallback && meta.toProvider) {
            detailBits.push(`using ${String(meta.toProvider)}`);
          }
          const details = showWork ? detailBits.join(' • ') : '';
          const noticeTitle = isContextOverflow
            ? 'Context limit reached'
            : isModelFallback
            ? 'Model switched for this message'
            : isLegacyRollover
              ? 'Context summarized + collapsed'
              : meta.kind === 'context_compacted'
                ? 'Context summarized'
                : 'Notice';
      return (
        <div
          key={block.id || idx}
          className={`p-2 rounded-md border text-xs ${
            isContextOverflow
              ? 'border-amber-200/80 bg-amber-50 text-amber-950'
              : 'border-blue-200/80 bg-blue-50 text-blue-900'
          }`}
        >
          <div className="text-xs font-medium">{noticeTitle}</div>
          <div className="text-xs mt-0.5 whitespace-pre-wrap">{text}</div>
          {details ? <div className={`text-[11px] mt-1 ${isContextOverflow ? 'text-amber-900/80' : 'text-blue-800/80'}`}>{details}</div> : null}
          {isContextOverflow && typeof onNewSession === 'function' ? (
            <button
              type="button"
              onClick={onNewSession}
              className="mt-2 inline-flex items-center rounded-md border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-medium text-amber-900 hover:bg-amber-100"
            >
              Start new chat
            </button>
          ) : null}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-1.5 mb-1.5">
      {orderedSegments.map((segment, idx) =>
        segment.kind === 'process' ? (
          <React.Fragment key={`activity-${idx}`}>
            {showWork ? (
              <ActivityGroup
                blocks={segment.blocks}
                theme={theme}
                isLatestGroup={idx === latestProcessSegmentIdx}
                activeThinkingBlockId={resolvedActiveThinkingBlockId}
                turnIsStreaming={turnIsStreaming}
              />
            ) : turnIsStreaming && idx === orderedSegments.length - 1 ? (
              <WorkingLine theme={theme} />
            ) : null}
            {isUser ? null : <AgentOutcomes blocks={segment.blocks} />}
          </React.Fragment>
        ) : (
          renderContentBlock(segment.block, idx)
        )
      )}
    </div>
  );
};

const MessageListContent = React.memo(function MessageListContent({
  messages,
  error,
  messagesEndRef,
  retryInput,
  setMessage,
  agentDisplayName = null,
  agentDescription = null,
  agentStarterPrompts = null,
  lastUserMessageRef = null,
  lastUserMessageIndex = -1,
  onNewSession = null,
  agentUi = null,
  showWork = true,
}) {
  const theme = DEFAULT_THEME;
  const config = DEFAULT_CONFIG;
  const displayTitle = agentDisplayName || config.title;
  const displaySubtitle =
    agentDescription != null
      ? agentDescription
      : config.subtitle;
  const suggestions =
    agentStarterPrompts != null
      ? agentStarterPrompts
      : config.suggestions;
  const [showEarlier, setShowEarlier] = useState(false);
  const firstMessageKey = messages[0]?.id || messages[0]?.timestamp || '';
  useEffect(() => {
    setShowEarlier(false);
  }, [firstMessageKey]);
  const folded = showEarlier ? 0 : foldedMessageCount(messages);

  return (
    <>
      {folded > 0 ? (
        <div className="flex justify-center pb-1">
          <button
            type="button"
            onClick={() => setShowEarlier(true)}
            className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-500 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-800"
            data-testid="show-earlier-messages"
          >
            Show {folded === 1 ? '1 earlier message' : `${folded} earlier messages`}
          </button>
        </div>
      ) : null}
      {messages.length === 0 ? (
        <div className="px-2 py-10 text-center text-gray-500">
          <div className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl ${theme.avatarAssistant} shadow-sm`}>
            {agentUi ? <AgentAvatar ui={agentUi} size="queue" /> : <LineSideIcon size={40} />}
          </div>
          <p className="text-base font-semibold text-gray-800">{displayTitle}</p>
          {displaySubtitle ? (
            <p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-gray-500">{displaySubtitle}</p>
          ) : null}
          {suggestions.length > 0 ? (
            <div className="mt-5 space-y-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Try asking</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {suggestions.map((s, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setMessage(s)}
                    className={`rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 shadow-sm transition-colors ${theme.suggestionBtn}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        messages.map((msg, index) => {
          if (index < folded) return null;
          const orderedBlocks = Array.isArray(msg.blocks) ? msg.blocks.filter(Boolean) : [];
          // A summary point reads as a divider in the conversation, not as a message.
          if (orderedBlocks.length > 0 && orderedBlocks.every(isCompactionNotice)) {
            return <CompactionDivider key={index} />;
          }
          // A thinking trace is "active" (still spinning) only while the turn is streaming and
          // the most recent block is that thinking beat. Once a tool/text follows, or the turn
          // finishes, its activity indicator stops. Details open only by user choice.
          const lastBlock = orderedBlocks[orderedBlocks.length - 1];
          const activeThinkingBlockId =
            msg.isStreaming && lastBlock?.type === 'thinking'
              ? resolveActiveThinkingBlockId(orderedBlocks)
              : null;
          const toolCallsFromMetadata =
            msg.metadata && Array.isArray(msg.metadata.tool_calls)
              ? msg.metadata.tool_calls.filter((tc) => tc && (tc.name || tc.function_name))
              : [];
          const allToolCalls = msg.toolCalls?.length > 0 ? msg.toolCalls : toolCallsFromMetadata;
          const hasToolCalls = allToolCalls.length > 0;

          const content = msg.content ? msg.content.trim() : '';
          const lowerContent = content.toLowerCase().replace(/\.$/, '');
          const isToolExecutionMsg = lowerContent === 'tool execution completed';
          const isRequestingMsg = /^requesting \d+ tool call\(s\)$/.test(lowerContent);
          const shouldShowContent = content && !isToolExecutionMsg && !isRequestingMsg;
          const isActivelyStreaming = msg.isStreaming || msg.streamingReasoning;

          if (
            !isActivelyStreaming &&
            !shouldShowContent &&
            !hasToolCalls &&
            orderedBlocks.length === 0 &&
            !msg.isError
          ) {
            return null;
          }

          const isUser = msg.role === 'user';
          // With how the agent works hidden, a finished reply that was only work has nothing to show.
          if (!showWork && !isUser && !isActivelyStreaming && !msg.isError
            && !(orderedBlocks.length > 0 ? showsWithoutWork(orderedBlocks) : shouldShowContent)) {
            return null;
          }
          const isLastUserMessage = isUser && index === lastUserMessageIndex;
          return (
            <div
              key={index}
              ref={isLastUserMessage ? lastUserMessageRef : null}
              className={`w-full min-w-0 flex ${isUser ? 'justify-end' : 'justify-start'} scroll-mt-2`}
            >
              <div className={`flex max-w-[99%] ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start gap-2 min-w-0`}>
                {/* The agent's own face, so the conversation reads as talking to it. */}
                {!isUser && agentUi ? (
                  <AgentAvatar ui={agentUi} size="xs" className="mt-0.5" />
                ) : (
                  <div
                    className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center overflow-hidden ${
                      isUser ? theme.avatarUser : theme.avatarAssistant
                    }`}
                  >
                    {msg.role === 'assistant' && <LineSideIcon size={22} />}
                    {msg.role === 'user' && <User size={14} className="text-white" />}
                  </div>
                )}
                <div
                  className={`flex-1 min-w-0 p-3 rounded-xl ${
                    msg.role === 'user'
                      ? `${theme.userBubble} text-white shadow-md`
                      : msg.isError
                        ? 'bg-red-50 text-red-800 border border-red-200'
                        : 'bg-white text-gray-800 border border-gray-100 shadow-sm'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    {msg.isStreaming && orderedBlocks.length === 0 && !shouldShowContent && (showWork || !msg.streamingReasoning?.trim()) && (
                      <div className="flex items-center gap-1.5 px-1 py-0.5">
                        <Loader2 size={13} className={`animate-spin ${theme.typingText}`} />
                        <span className={`text-xs font-medium ${theme.typingText}`}>{showWork ? 'Thinking…' : 'Working on it…'}</span>
                      </div>
                    )}
                    {orderedBlocks.length > 0 && (
                      <OrderedBlocks
                        blocks={orderedBlocks}
                        isUser={isUser}
                        theme={theme}
                        activeThinkingBlockId={activeThinkingBlockId}
                        turnIsStreaming={Boolean(msg.isStreaming || msg.streamingReasoning)}
                        onNewSession={onNewSession}
                        showWork={showWork}
                      />
                    )}
                    {orderedBlocks.length === 0 && msg.streamingReasoning?.trim() && (showWork ? (
                      <ActivityThinkingRow
                        block={{ id: activeThinkingBlockId || 'streaming-reasoning', text: msg.streamingReasoning }}
                        isActive={true}
                        theme={theme}
                      />
                    ) : <WorkingLine theme={theme} />)}
                    {orderedBlocks.length === 0 && hasToolCalls && !showWork && msg.isStreaming ? <WorkingLine theme={theme} /> : null}
                    {orderedBlocks.length === 0 && hasToolCalls && showWork && (
                      <ActivityGroup
                        blocks={allToolCalls.map((tc, tcIdx) => ({
                          id: `metatool-${tcIdx}`,
                          type: 'tool_call',
                          toolName: tc.name || tc.function_name,
                          status: tc.status || 'completed',
                          args: tc.args || tc.arguments || {},
                          result: tc.result ?? null,
                        }))}
                        theme={theme}
                        turnIsStreaming={Boolean(msg.isStreaming || msg.streamingReasoning)}
                      />
                    )}
                    {orderedBlocks.length === 0 && shouldShowContent && (
                      <div className={`px-2 pb-2 pt-0 text-sm ${msg.role === 'user' ? 'text-white' : 'text-gray-800'}`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={
                            msg.role === 'user' ? USER_MARKDOWN_COMPONENTS : createMarkdownComponents(false, theme)
                          }
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                  {msg.timestamp && (
                    <div
                      className={`text-[10px] mt-1 ${msg.role === 'user' ? 'text-white' : 'text-gray-500'}`}
                    >
                      {formatClock(msg.timestamp, { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                      {msg.role === 'user' && msg.metadata?.delivery_status ? (
                        <span className="ml-2" role="status">{({ sending: 'Sending…', accepted: 'Accepted', received: 'Received', uncertain: 'Delivery unconfirmed', failed: 'Not sent' })[msg.metadata.delivery_status]}</span>
                      ) : null}
                      {msg.metadata?.delivery_status === 'uncertain' && retryInput ? (
                        <button type="button" className="ml-2 underline" onClick={() => retryInput(msg.metadata.client_input_id)}>Retry delivery</button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}

      {error && (
        <div className="w-full">
          <div role="alert" className="bg-red-100 text-red-800 p-3 rounded-lg text-sm">{error}</div>
        </div>
      )}

      <div ref={messagesEndRef} />
    </>
  );
});

/**
 * Chat UI for a conversation with an agent and historical conversation blocks.
 */
const BaseAgentChat = ({
  allowAttachments = true,
  maxMessageLength,
  messages,
  isLoading,
  disabled = false,
  error,
  sendMessage,
  retryInput,
  cancelStream,
  isStreaming,
  canStop = true,
  message,
  setMessage,
  inputRef,
  messagesEndRef,
  latestContextUsage,
  isLoadingContextUsage = false,
  agentDisplayName = null,
  agentDescription = null,
  agentStarterPrompts = null,
  onNewSession = null,
  showContextUsage = true,
  agentUi = null,
  // Off: the conversation only. On: also how the agent works (steps, thinking, tools, context use).
  showWork = true,
  composerContextKey,
}) => {
  const theme = DEFAULT_THEME;
  const config = DEFAULT_CONFIG;
  const inputPlaceholder =
    agentDisplayName
      ? `Ask ${agentDisplayName} or assign work…`
      : config.placeholder;
  const messagesContainerRef = useRef(null);
  const lastUserMessageRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const userScrolledAwayRef = useRef(false);
  const turnOverflowedRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [bottomSpacer, setBottomSpacer] = useState(0);
  const [voiceSettingsOpen, setVoiceSettingsOpen] = useState(false);
  const voice = useVoiceInput({ message, setMessage, disabled: Boolean(isLoading || disabled), contextKey: composerContextKey, maxMessageLength });
  const replaceDraft = useCallback((text) => { voice.cancel(); setMessage(text); }, [voice.cancel, setMessage]);

  useLayoutEffect(() => {
    const input = inputRef?.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
  }, [message, inputRef]);

  const scrollToBottomIfNeeded = () => {
    if (!shouldAutoScrollRef.current) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const target = container.scrollHeight - container.clientHeight;
    if (container.scrollTop >= target - 2) return;

    isProgrammaticScrollRef.current = true;
    container.scrollTop = target;
    queueMicrotask(() => {
      isProgrammaticScrollRef.current = false;
    });
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return undefined;

    const observer = new ResizeObserver(() => {
      if (turnOverflowedRef.current) {
        scrollToBottomIfNeeded();
      }
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  const lastUserMessageIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'user') return i;
    }
    return -1;
  }, [messages]);

  const userMessageCount = useMemo(
    () => messages.reduce((count, msg) => (msg?.role === 'user' ? count + 1 : count), 0),
    [messages]
  );
  const prevUserMessageCountRef = useRef(userMessageCount);

  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight <= 48;
  };

  const handleMessagesScroll = () => {
    if (isProgrammaticScrollRef.current) return;
    shouldAutoScrollRef.current = isNearBottom();
    userScrolledAwayRef.current = !shouldAutoScrollRef.current;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmedMessage = message.trim();
    if ((!trimmedMessage && pendingFiles.length === 0) || isLoading || disabled || voice.isActive()) return;
    const filesToSend = [...pendingFiles];
    setMessage('');
    setPendingFiles([]);
    await sendMessage(trimmedMessage, {}, { files: filesToSend });
  };

  const addPendingFiles = (fileList) => {
    if (!allowAttachments) return;
    const selected = Array.from(fileList || []).filter(Boolean);
    if (selected.length === 0) return;
    setPendingFiles((prev) => [...prev, ...selected]);
  };

  const handleFileSelection = (event) => {
    addPendingFiles(event.target.files);
    event.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, idx) => idx !== index));
  };

  const hasFilesInDrag = (event) =>
    Array.from(event.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = (event) => {
    if (!allowAttachments || !hasFilesInDrag(event) || isLoading || disabled) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  };

  const handleDragLeave = (event) => {
    if (!hasFilesInDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
    }
  };

  const handleDragOver = (event) => {
    if (!allowAttachments || !hasFilesInDrag(event) || isLoading || disabled) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    if (isLoading || disabled) return;
    addPendingFiles(event.dataTransfer?.files);
  };

  const canSend = (message.trim().length > 0 || pendingFiles.length > 0) && !isLoading && !disabled && !voice.active;

  // New user turn: anchor that message to the top (Cursor/ChatGPT-style) and reserve
  // space below so the response can grow downward from there instead of chasing the bottom.
  useLayoutEffect(() => {
    if (userMessageCount <= prevUserMessageCountRef.current) {
      prevUserMessageCountRef.current = userMessageCount;
      return;
    }
    prevUserMessageCountRef.current = userMessageCount;

    const container = messagesContainerRef.current;
    const userMsg = lastUserMessageRef.current;
    if (!container || !userMsg) return;

    shouldAutoScrollRef.current = false;
    userScrolledAwayRef.current = false;
    turnOverflowedRef.current = false;
    setBottomSpacer(container.clientHeight);
    requestAnimationFrame(() => {
      const top = Math.max(0, userMsg.offsetTop - 8);
      isProgrammaticScrollRef.current = true;
      container.scrollTo({ top, behavior: 'instant' });
      requestAnimationFrame(() => { isProgrammaticScrollRef.current = false; });
    });
  }, [userMessageCount]);

  // Keep the reserved space exactly large enough to hold the latest turn at the top.
  // As the response streams in, the spacer shrinks toward zero; once the turn overflows
  // the viewport we fall back to following the bottom. If the turn shrinks again (e.g.
  // thinking collapses), recover top-mount mode instead of staying latched to the bottom.
  // The turn height is measured from the end marker (which sits *before* the spacer), so
  // the calculation is independent of the spacer itself — avoiding feedback loops.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    const userMsg = lastUserMessageRef.current;
    const endMarker = messagesEndRef?.current;

    if (!container || !userMsg || !endMarker) {
      turnOverflowedRef.current = false;
      setBottomSpacer((prev) => (prev !== 0 ? 0 : prev));
      return;
    }

    const turnHeight = endMarker.offsetTop - userMsg.offsetTop;
    const viewportBudget = container.clientHeight - 8;

    if (turnHeight >= viewportBudget) {
      if (!turnOverflowedRef.current) {
        turnOverflowedRef.current = true;
        setBottomSpacer(0);
        shouldAutoScrollRef.current = !userScrolledAwayRef.current;
      }
      if (!userScrolledAwayRef.current) scrollToBottomIfNeeded();
      return;
    }

    const wasOverflowed = turnOverflowedRef.current;
    turnOverflowedRef.current = false;
    shouldAutoScrollRef.current = false;

    const needed = Math.max(0, viewportBudget - turnHeight);
    setBottomSpacer((prev) => (Math.abs(needed - prev) > 1 ? needed : prev));

    if (wasOverflowed && !userScrolledAwayRef.current) {
      isProgrammaticScrollRef.current = true;
      container.scrollTop = Math.max(0, userMsg.offsetTop - 8);
      queueMicrotask(() => {
        isProgrammaticScrollRef.current = false;
      });
    }
  }, [messages, messagesEndRef]);

  return (
    <div
      className="relative flex flex-col flex-1 min-h-0"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDraggingFiles && (
        <div
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-lg border-2 border-dashed border-sky-400 bg-sky-50/90"
          aria-hidden
        >
          <div className="flex flex-col items-center gap-2 text-sky-700">
            <Paperclip size={28} />
            <span className="text-sm font-medium">Drop files to attach</span>
          </div>
        </div>
      )}
      {showContextUsage && showWork ? <ContextUsageBar usage={latestContextUsage} isLoading={isLoadingContextUsage} /> : null}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="relative flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-3 w-full"
      >
        <div className="space-y-3">
        <MessageListContent
          messages={messages}
          error={error}
          messagesEndRef={messagesEndRef}
          retryInput={retryInput}
          setMessage={replaceDraft}
          agentDisplayName={agentDisplayName}
          agentDescription={agentDescription}
          agentStarterPrompts={agentStarterPrompts}
          lastUserMessageRef={lastUserMessageRef}
          lastUserMessageIndex={lastUserMessageIndex}
          onNewSession={onNewSession}
          agentUi={agentUi}
          showWork={showWork}
        />
        </div>
        <div aria-hidden style={{ minHeight: bottomSpacer }} />
      </div>
      <div className="flex-shrink-0 px-2 py-3 bg-white border-t border-gray-200">
        <div>
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2 px-1">
            {pendingFiles.map((file, index) => (
              <div
                key={`${file.name}-${file.lastModified}-${index}`}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-700"
              >
                <FileText size={12} />
                <span className="truncate max-w-[180px]">{file.name}</span>
                <button
                  type="button"
                  onClick={() => removePendingFile(index)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label={`Remove ${file.name}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        <form
          onSubmit={handleSend}
          className={`relative flex items-end gap-1.5 rounded-2xl border bg-gray-50/80 p-1.5 shadow-sm transition-all focus-within:bg-white focus-within:shadow-md focus-within:ring-2 ${
            isDraggingFiles
              ? 'border-sky-400 ring-2 ring-sky-100'
              : `border-gray-200/80 ${theme.inputFocus}`
          }`}
        >
          {allowAttachments && <><input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelection}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading || disabled}
            className={`p-2.5 rounded-xl transition-all flex items-center justify-center flex-shrink-0 ${
              isLoading ? 'text-gray-300' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title="Attach files (or drag and drop)"
          >
            <Paperclip size={16} />
          </button></>}
          {/* 16px on phones, so iOS does not zoom the page when the composer is focused. */}
          <textarea
            maxLength={maxMessageLength}
            ref={inputRef}
            value={message}
            disabled={disabled}
            readOnly={voice.active}
            dir="auto"
            onChange={(e) => {
              setMessage(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && voice.active) {
                e.preventDefault();
                e.stopPropagation();
                voice.cancel();
              }
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            placeholder={inputPlaceholder}
            className="min-w-0 flex-1 bg-transparent px-3 py-2 resize-none focus:outline-none overflow-y-auto max-h-[120px] min-h-[2.25rem] text-gray-700 placeholder-gray-400 text-base sm:text-xs"
            rows={1}
          />
          <VoiceInputButtons voice={voice} disabled={Boolean(isLoading || disabled)} settingsOpen={voiceSettingsOpen} onToggleSettings={() => setVoiceSettingsOpen((open) => !open)} />
          <button
            type="submit"
            disabled={!canSend}
            className={`p-2.5 rounded-xl transition-all flex items-center justify-center flex-shrink-0 ${
              !canSend ? 'text-gray-300 bg-transparent' : `${theme.sendBtn} text-white shadow-sm active:scale-95`
            }`}
            title={isLoading ? 'Sending...' : 'Send message'}
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
          {isStreaming && canStop && (
            <button
              type="button"
              onClick={cancelStream}
              className="px-2 py-1.5 rounded-lg text-sm font-medium border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
              title="Stop"
            >
              Stop
            </button>
          )}
        </form>
        <VoiceInputDetails voice={voice} settingsOpen={voiceSettingsOpen} />
        </div>
      </div>
    </div>
  );
};

export default BaseAgentChat;
